import React from 'react';
import { Plus, Trash2, Users, Clock, Target, Mail, Hash, Calendar } from 'lucide-react';

interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface Rules {
  operator: 'AND' | 'OR';
  conditions: Condition[];
}

interface RuleBuilderProps {
  rules: Rules;
  onChange: (rules: Rules) => void;
  type: 'inclusion' | 'exclusion';
}

interface FieldOption {
  value: string;
  label: string;
  type: 'string' | 'number' | 'array' | 'date' | 'boolean';
  icon?: React.ReactNode;
  description?: string;
}

const FIELD_OPTIONS: FieldOption[] = [
  { value: 'email', label: 'Email', type: 'string', icon: <Mail size={16} />, description: 'Lead email address' },
  { value: 'parent_name', label: 'Parent Name', type: 'string', icon: <Users size={16} /> },
  { value: 'child_name', label: 'Child Name', type: 'string', icon: <Users size={16} /> },
  { value: 'child_age', label: 'Child Age', type: 'number', icon: <Hash size={16} /> },
  { value: 'lead_score', label: 'Lead Score', type: 'number', icon: <Target size={16} />, description: '0-100 lead quality score' },
  { value: 'timeline', label: 'Timeline', type: 'string', icon: <Clock size={16} />, description: 'When they want to enroll' },
  { value: 'special_needs', label: 'Special Needs', type: 'array', icon: <Users size={16} />, description: 'Child\'s special needs' },
  { value: 'current_support', label: 'Current Support', type: 'array', icon: <Users size={16} />, description: 'IEP, 504, therapy services' },
  { value: 'phone', label: 'Phone', type: 'string', icon: <Mail size={16} /> },
  { value: 'biggest_concerns', label: 'Biggest Concerns', type: 'string', icon: <Users size={16} /> },
  { value: 'created_at', label: 'Created Date', type: 'date', icon: <Calendar size={16} />, description: 'When lead was created' },
  { value: 'all_special_needs', label: 'All Children Special Needs', type: 'array', icon: <Users size={16} />, description: 'Special needs from all children' },
  { value: 'child_count', label: 'Number of Children', type: 'number', icon: <Hash size={16} />, description: 'Total children count' },
  { value: 'all_ages', label: 'All Children Ages', type: 'array', icon: <Hash size={16} />, description: 'Ages of all children' },
];

const OPERATORS = {
  string: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    { value: 'matches_regex', label: 'matches pattern' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
    { value: 'greater_than_or_equal', label: 'greater than or equal' },
    { value: 'less_than_or_equal', label: 'less than or equal' },
  ],
  array: [
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  date: [
    { value: 'days_since', label: 'days since' },
    { value: 'greater_than', label: 'after' },
    { value: 'less_than', label: 'before' },
  ],
  boolean: [
    { value: 'equals', label: 'is' },
  ]
};

const SPECIAL_NEEDS_OPTIONS = [
  'Autism/ASD',
  'ADHD',
  'Learning Disabilities',
  'Dyslexia',
  'Speech/Language Delays',
  'Sensory Processing',
  'Anxiety/Depression',
  'Other'
];

const TIMELINE_OPTIONS = [
  'Immediately',
  'Next semester',
  'Next school year',
  'Just exploring'
];

const CURRENT_SUPPORT_OPTIONS = [
  'IEP',
  '504 Plan',
  'Speech therapy',
  'OT',
  'Behavioral therapy',
  'Tutoring',
  'None currently'
];

const RuleBuilder: React.FC<RuleBuilderProps> = ({ rules, onChange, type }) => {
  // Ensure rules has proper structure with defaults
  const safeRules = {
    operator: rules?.operator || 'AND',
    conditions: rules?.conditions || []
  };

  const addCondition = () => {
    const newCondition: Condition = {
      field: 'special_needs',
      operator: 'contains',
      value: ''
    };

    onChange({
      ...safeRules,
      conditions: [...safeRules.conditions, newCondition]
    });
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...safeRules.conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    
    // Reset value when field or operator changes
    if (updates.field || updates.operator) {
      newConditions[index].value = '';
    }
    
    onChange({
      ...safeRules,
      conditions: newConditions
    });
  };

  const removeCondition = (index: number) => {
    onChange({
      ...safeRules,
      conditions: safeRules.conditions.filter((_, i) => i !== index)
    });
  };

  const toggleOperator = () => {
    onChange({
      ...safeRules,
      operator: safeRules.operator === 'AND' ? 'OR' : 'AND'
    });
  };

  const getFieldType = (fieldValue: string): 'string' | 'number' | 'array' | 'date' | 'boolean' => {
    const field = FIELD_OPTIONS.find(f => f.value === fieldValue);
    return field?.type || 'string';
  };

  const getOperatorOptions = (fieldType: string) => {
    return OPERATORS[fieldType as keyof typeof OPERATORS] || OPERATORS.string;
  };

  const renderValueInput = (condition: Condition, index: number) => {
    const fieldType = getFieldType(condition.field);
    const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);

    if (!needsValue) {
      return null;
    }

    // Special value inputs for specific fields
    if (condition.field === 'special_needs' || condition.field === 'all_special_needs') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-40"
        >
          <option value="">Select special need...</option>
          {SPECIAL_NEEDS_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (condition.field === 'timeline') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-40"
        >
          <option value="">Select timeline...</option>
          {TIMELINE_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (condition.field === 'current_support') {
      return (
        <select
          value={condition.value}
          onChange={(e) => updateCondition(index, { value: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-40"
        >
          <option value="">Select support type...</option>
          {CURRENT_SUPPORT_OPTIONS.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    // Number inputs
    if (fieldType === 'number') {
      return (
        <input
          type="number"
          value={condition.value}
          onChange={(e) => updateCondition(index, { value: Number(e.target.value) })}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm w-24"
          placeholder="0"
        />
      );
    }

    // Date inputs
    if (fieldType === 'date' && condition.operator === 'days_since') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={condition.value}
            onChange={(e) => updateCondition(index, { value: Number(e.target.value) })}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm w-20"
            placeholder="30"
          />
          <span className="text-sm text-gray-500">days ago</span>
        </div>
      );
    }

    // Default string input
    return (
      <input
        type="text"
        value={condition.value}
        onChange={(e) => updateCondition(index, { value: e.target.value })}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-40"
        placeholder="Enter value..."
      />
    );
  };

  const getColorScheme = () => {
    return type === 'inclusion' 
      ? { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' }
      : { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' };
  };

  const colors = getColorScheme();

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
      {/* Operator Toggle */}
      {safeRules.conditions.length > 1 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600">Match</span>
          <button
            onClick={toggleOperator}
            className={`px-3 py-1 rounded text-sm font-medium ${
              safeRules.operator === 'AND' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {safeRules.operator}
          </button>
          <span className="text-sm text-gray-600">of the following conditions:</span>
        </div>
      )}

      {/* Conditions */}
      <div className="space-y-3">
        {safeRules.conditions.map((condition, index) => {
          const fieldType = getFieldType(condition.field);
          const operatorOptions = getOperatorOptions(fieldType);
          const fieldOption = FIELD_OPTIONS.find(f => f.value === condition.field);

          return (
            <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border">
              {/* Field Selection */}
              <div className="flex items-center gap-2 min-w-48">
                {fieldOption?.icon}
                <select
                  value={condition.field}
                  onChange={(e) => updateCondition(index, { field: e.target.value, operator: 'contains' })}
                  className="border-none bg-transparent text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  {FIELD_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Operator Selection */}
              <select
                value={condition.operator}
                onChange={(e) => updateCondition(index, { operator: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-36"
              >
                {operatorOptions.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>

              {/* Value Input */}
              {renderValueInput(condition, index)}

              {/* Remove Button */}
              <button
                onClick={() => removeCondition(index)}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                title="Remove condition"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Add Condition */}
      <div className="mt-4">
        <button
          onClick={addCondition}
          className={`flex items-center gap-2 px-3 py-2 ${colors.text} border ${colors.border} rounded-lg hover:bg-white transition-colors`}
        >
          <Plus size={16} />
          Add {type === 'inclusion' ? 'Inclusion' : 'Exclusion'} Rule
        </button>
      </div>

      {/* Empty State */}
      {safeRules.conditions.length === 0 && (
        <div className="text-center py-8">
          <Target size={32} className={`mx-auto ${colors.text} mb-2`} />
          <p className={`${colors.text} text-sm mb-3`}>
            No {type} rules defined
          </p>
          <p className="text-xs text-gray-500 mb-4">
            {type === 'inclusion' 
              ? 'Define who should be included in this segment'
              : 'Define who should be excluded from this segment (optional)'
            }
          </p>
          <button
            onClick={addCondition}
            className={`${colors.text} border ${colors.border} px-4 py-2 rounded-lg hover:bg-white transition-colors`}
          >
            <Plus size={16} className="inline mr-2" />
            Add First Rule
          </button>
        </div>
      )}

      {/* Rules Summary */}
      {safeRules.conditions.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm">
          <strong>Rule Summary:</strong> {type === 'inclusion' ? 'Include' : 'Exclude'} leads where{' '}
          {safeRules.conditions.map((condition, index) => {
            const fieldOption = FIELD_OPTIONS.find(f => f.value === condition?.field);
            const operatorOption = getOperatorOptions(getFieldType(condition?.field || 'string'))
              .find(op => op.value === condition?.operator);
            
            return (
              <span key={index}>
                {index > 0 && ` ${safeRules.operator?.toLowerCase() || 'and'} `}
                <span className="font-medium">{fieldOption?.label || 'Unknown Field'}</span>
                {' '}{operatorOption?.label || 'Unknown Operator'}
                {condition?.value && ` "${condition.value}"`}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RuleBuilder;